import Head from 'next/head';

import { useMemo } from 'react';

import { urlHelpers } from '@nocowanie/core/helpers';
import * as formatHelpers from '@nocowanie/core/helpers/format.helpers';

import { environment } from '@app/environments/environment';
import { apiTypes } from '@app/models';
import { Currency } from '@app/models/api/api-types';
import { accommodationSlice } from '@app/state/slices';
import * as accommodationStaticSlice from '@app/state/slices/accommodation-static.slice';
import { useAppSelector } from '@app/state/state';

import { RichSnippetsProps } from './rich-snippets.props';

import {
    checkIfPetsAllowed,
    createAddress,
    createAggregateRating,
    createAmenityFeatures,
    createGeo,
    createImageVariants,
    trimDescription,
} from '../json-ld.utils';

export const RichSnippets = ({ accommodationId }: RichSnippetsProps) => {
    const {
        type,
        name,
        description,
        image,
        addressCountry,
        addressLocality,
        addressRegion,
        postalCode,
        streetAddress,
        latitude,
        longitude,
        ratingAverage,
        ratingCount,
        services,
        internalAmenityGroups,
        labels,
        currency,
    } = useAppSelector(accommodationStaticSlice.selectors.selectRichSnippetsData);
    const accommodation = useAppSelector(accommodationSlice.selectors.selectCurrentAccommodation);

    // convertedCurrency isn't in ElasticSearch, so we set the default value
    const convertedCurrencyCode = accommodation?.convertedRatesCurrency ?? currency ?? Currency.Pln;

    const availableApartments = useMemo(
        () =>
            accommodation?.apartments?.filter(
                apartment => apartment?.variants && apartment.variants.length > 0,
            ),
        [accommodation?.apartments],
    );

    const numberOfRooms = availableApartments?.length;

    const priceRange = useMemo(() => {
        const variants = availableApartments?.flatMap(apartment => apartment?.variants ?? []) ?? [];
        const prices = variants
            .map(variant => variant?.convertedTotalPrice ?? variant?.totalPrice)
            .filter((price): price is number => !!price);

        if (prices.length === 0) {
            return null;
        }

        const from = Math.min(...prices);
        const to = Math.max(...prices);

        return formatHelpers.priceRange({ from, to }, convertedCurrencyCode);
    }, [availableApartments, convertedCurrencyCode]);

    const url = useMemo(() => {
        return name && accommodationId
            ? urlHelpers.getAccommodationUrl(accommodationId, name, {}, environment.NOCOWANIE_URL)
            : undefined;
    }, [accommodationId, name]);

    const displayedType = useMemo(() => {
        switch (type) {
            case apiTypes.HotelTypeEnum['Hotel']:
                return apiTypes.HotelTypeEnum['Hotel'];
            case apiTypes.HotelTypeEnum['Hostel']:
                return apiTypes.HotelTypeEnum['Hostel'];
            case apiTypes.HotelTypeEnum['Motel']:
                return apiTypes.HotelTypeEnum['Motel'];
            default:
                return 'LodgingBusiness';
        }
    }, [type]);

    const imageVariants = image ? createImageVariants(image) : undefined;

    const trimmedDesc = description ? trimDescription(description) : undefined;

    // we need to remap 'PLN' to 'zł'
    const petsAllowed = checkIfPetsAllowed({
        labels: labels ?? [],
        internalAmenityGroups: internalAmenityGroups ?? [],
        services: services ?? [],
    });
    const address = createAddress({
        addressCountry,
        addressLocality,
        addressRegion,
        postalCode,
        streetAddress,
    });
    const geo = latitude && longitude ? createGeo({ latitude, longitude }) : null;
    const aggregateRating =
        ratingAverage && ratingCount ? createAggregateRating({ ratingAverage, ratingCount }) : null;
    const amenityFeature = services ? createAmenityFeatures({ services }) : null;

    if (!type || !name) return;

    const schemaObj = {
        '@context': 'https://schema.org/',
        '@type': displayedType,
        name,
        ...(trimmedDesc ? { description: trimmedDesc } : null),
        ...(numberOfRooms ? { numberOfRooms } : null),
        ...(url ? { tourBookingPage: url } : null),
        petsAllowed,
        ...(imageVariants ? { image: imageVariants } : null),
        ...(address ? { address } : null),
        ...(geo ? { geo } : null),
        ...(aggregateRating ? { aggregateRating } : null),
        ...(amenityFeature ? { amenityFeature } : null),
        ...(latitude && longitude
            ? { hasMap: `https://www.openstreetmap.org/#map=15/${latitude}/${longitude}` }
            : null),
        ...(priceRange ? { priceRange } : null),
    };

    return (
        <Head>
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaObj) }}
            />
        </Head>
    );
};
